"use client";

import React from "react";
import { Check, Star, Minus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const tiers = [{
  id: "essential",
  name: "Sustainer",
  price: "$50/mo",
  description: "Provide consistent stability for individuals navigating reentry.",
  isPopular: false,
  buttonText: "Join as Sustainer",
  buttonLink: "/SupportUsPage"
}, {
  id: "signature",
  name: "Legacy Founder",
  price: "$5,000+",
  description: "Build the foundation for long-term transformational impact.",
  isPopular: true,
  buttonText: "Become a Founder",
  buttonLink: "/SupportUsPage"
}, {
  id: "bespoke",
  name: "Visionary",
  price: "Custom",
  description: "Strategic partnership to scale our community resource navigation.",
  isPopular: false,
  buttonText: "Partner With Us",
  buttonLink: "/SupportUsPage"
}];
const tierFeatures = [{
  tierId: "essential",
  text: "Monthly mentorship session funding"
}, {
  tierId: "essential",
  text: "Transportation assistance credits"
}, {
  tierId: "essential",
  text: "Quarterly impact newsletters"
}, {
  tierId: "signature",
  text: "Permanent recognition in the Pathways Center"
}, {
  tierId: "signature",
  text: "Founding member of the Mary E. Jiles initiative"
}, {
  tierId: "signature",
  text: "VIP access to annual community galas"
}, {
  tierId: "signature",
  text: "Direct sponsorship of a housing priority"
}, {
  tierId: "bespoke",
  text: "Naming rights for specific program areas"
}, {
  tierId: "bespoke",
  text: "Strategic advisory board invitation"
}, {
  tierId: "bespoke",
  text: "Customized annual impact reporting"
}, {
  tierId: "bespoke",
  text: "Legacy endowment opportunities"
}];
const comparisonFeatures = [{
  id: "f1",
  name: "Impact Focus"
}, {
  id: "f2",
  name: "Recognition"
}, {
  id: "f3",
  name: "Center Access"
}, {
  id: "f4",
  name: "Strategic Input"
}, {
  id: "f5",
  name: "Reporting"
}];
const comparisonValues = [{
  featureId: "f1",
  essential: "Direct Support",
  signature: "Infrastructure",
  bespoke: "Systemic Change"
}, {
  featureId: "f2",
  essential: "Digital Wall",
  signature: "Physical Plaque",
  bespoke: "Naming Rights"
}, {
  featureId: "f3",
  essential: "Add-on",
  signature: "Included",
  bespoke: "Included"
}, {
  featureId: "f4",
  essential: "Add-on",
  signature: "Annual Meeting",
  bespoke: "Board Seat"
}, {
  featureId: "f5",
  essential: "Standard (Annual)",
  signature: "Standard (Annual)",
  bespoke: "Priority (Quarterly)"
}];
export default function InvestmentOverview() {
  const headerReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const cardsReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const tableReveal = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="242442" id="the-founders-circle" className="dark bg-background py-24 md:py-32 overflow-hidden text-foreground">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {}
        <div ref={headerReveal.ref} className={cn("text-center max-w-3xl mx-auto mb-16 md:mb-24 transition-all duration-1000", headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <h2 className="font-default text-4xl md:text-5xl font-bold text-foreground mb-6 tracking-tight">
            The Founders Circle
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-serif leading-relaxed">
            Join a special group of initial supporters helping build the foundation for long-term impact, 
            future growth, and transformational community support.
          </p>
        </div>

        {}
        <div ref={cardsReveal.ref} className={cn("grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12 mb-24 transition-all duration-1000 delay-200", cardsReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          {tiers.map((tier, index) => {
          const features = tierFeatures.filter(f => f.tierId === tier.id);
          return <div key={tier.id} data-index={index} className="relative flex flex-col">
                {tier.isPopular && <div className="absolute -top-4 left-0 right-0 flex justify-center z-10">
                    <Badge className="bg-accent text-accent-foreground px-4 py-1 text-sm font-semibold uppercase tracking-wider shadow-md border-none">
                      <Star className="w-4 h-4 mr-1.5 fill-current" />
                      Most Impactful
                    </Badge>
                  </div>}
                
                <Card className={cn("flex-1 flex flex-col bg-card text-card-foreground border-border shadow-2xl overflow-hidden transition-transform duration-300 hover:-translate-y-2", tier.isPopular ? "ring-2 ring-accent md:scale-105 z-0 border-accent" : "mt-4 md:mt-0")}>
                  <CardHeader className="text-center pb-8 pt-10">
                    <CardTitle className="font-default text-2xl md:text-3xl font-semibold text-primary mb-2">
                      {tier.name}
                    </CardTitle>
                    <div className="mt-4 flex items-baseline justify-center gap-x-2">
                      <span className="text-4xl md:text-5xl font-bold tracking-tight text-foreground">
                        {tier.price}
                      </span>
                    </div>
                    <CardDescription className="text-muted-foreground mt-4 font-serif text-base">
                      {tier.description}
                    </CardDescription>
                  </CardHeader>
                  
                  <CardContent className="flex-1 px-8">
                    <ul className="space-y-4">
                      {features.map((feature, fIndex) => <li key={fIndex} data-index={fIndex} className="flex items-start gap-3">
                          <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                          <span className="text-foreground/90 leading-tight font-sans">
                            {feature.text}
                          </span>
                        </li>)}
                    </ul>
                  </CardContent>
                  
                  <CardFooter className="pt-8 pb-10 px-8">
                    <Button asChild variant={tier.isPopular ? "default" : "outline"} className={cn("w-full h-12 text-base font-semibold rounded-full", tier.isPopular ? "bg-primary text-primary-foreground hover:bg-primary/90" : "border-primary text-primary hover:bg-primary hover:text-primary-foreground bg-transparent")}>
                      <Link to={tier.buttonLink}>{tier.buttonText}</Link>
                    </Button>
                  </CardFooter>
                </Card>
              </div>;
        })}
        </div>

        {}
        <div ref={tableReveal.ref} className={cn("max-w-5xl mx-auto transition-all duration-1000 delay-400", tableReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          <div className="text-center mb-10">
            <h3 className="font-default text-3xl font-bold text-foreground mb-4">
              Impact Comparison
            </h3>
            <p className="text-muted-foreground font-serif">
              A detailed look at how your contribution fuels the mission of The Trust Foundation.
            </p>
          </div>

          <div className="bg-card rounded-3xl shadow-xl border border-border overflow-hidden">
            <Table>
              <TableHeader className="bg-muted/50">
                <TableRow className="hover:bg-transparent border-border">
                  <TableHead className="w-[30%] font-default text-foreground font-semibold py-5 pl-6">
                    Milestones
                  </TableHead>
                  {tiers.map((tier, index) => <TableHead key={tier.id} data-index={index} className="text-center font-default text-foreground font-semibold py-5">
                      {tier.name}
                    </TableHead>)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {comparisonFeatures.map((feature, index) => {
                const values = comparisonValues.find(v => v.featureId === feature.id);
                return <TableRow key={feature.id} data-index={index} className="border-border hover:bg-muted/30 transition-colors">
                      <TableCell className="font-medium text-foreground py-4 pl-6 font-sans">
                        {feature.name}
                      </TableCell>
                      <TableCell className="text-center text-muted-foreground py-4 font-sans">
                        {values?.essential === "Add-on" ? <span className="text-muted-foreground/60 italic">Available</span> : values?.essential}
                      </TableCell>
                      <TableCell className="text-center text-foreground py-4 font-medium font-sans">
                        {values?.signature === "Included" ? <Check className="w-5 h-5 text-primary mx-auto" /> : values?.signature === "Add-on" ? <span className="text-muted-foreground/60 italic">Available</span> : values?.signature}
                      </TableCell>
                      <TableCell className="text-center text-muted-foreground py-4 font-medium font-sans">
                        {values?.bespoke === "Included" ? <Check className="w-5 h-5 text-primary mx-auto" /> : values?.bespoke}
                      </TableCell>
                    </TableRow>;
              })}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </section>;
}